import { useEffect, useMemo, useState, useCallback } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, isToday } from "date-fns";
import { ChevronLeft, ChevronRight, Search, Trash2, CalendarDays } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ymd = (d) => format(d, "yyyy-MM-dd");
const within = (day, start, end) => day >= start && day <= end;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function FleetCalendar({ cars, onMutate }) {
  const listed = useMemo(() => cars.filter((c) => c.review_status !== "rejected"), [cars]);
  const [q, setQ] = useState("");
  const [carId, setCarId] = useState(null);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [schedule, setSchedule] = useState({ blocks: [], bookings: [] });
  const [rate, setRate] = useState("");
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!carId && listed.length) setCarId(listed[0].id); }, [listed, carId]);

  const car = listed.find((c) => c.id === carId);

  const loadSchedule = useCallback(() => {
    if (!carId) return;
    api.get(`/admin/cars/${carId}/schedule`)
      .then((r) => { setSchedule(r.data); setRate(String(r.data.weekly_rate ?? "")); })
      .catch(() => {});
  }, [carId]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const filtered = listed.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const days = useMemo(() => eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) }), [month]);
  const leading = startOfMonth(month).getDay();

  const dayStatus = (d) => {
    const s = ymd(d);
    if (schedule.bookings.some((b) => within(s, b.pickup_date, b.return_date))) return "booked";
    if (schedule.blocks.some((b) => within(s, b.start, b.end))) return "blocked";
    return "open";
  };

  const clickDay = (d) => {
    const s = ymd(d);
    if (!selStart || (selStart && selEnd)) { setSelStart(s); setSelEnd(null); }
    else if (s < selStart) setSelStart(s);
    else setSelEnd(s);
  };
  const inSel = (d) => {
    const s = ymd(d);
    if (selStart && selEnd) return within(s, selStart, selEnd);
    return s === selStart;
  };

  const blockDates = async () => {
    if (!selStart) return;
    const start = selStart, end = selEnd || selStart;
    setSaving(true);
    try {
      await api.post(`/admin/cars/${carId}/blocks`, { start, end, reason: note || "Blocked" });
      toast.success("Dates blocked");
      setSelStart(null); setSelEnd(null); setNote("");
      loadSchedule(); onMutate?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeBlock = async (bid) => {
    try { await api.delete(`/admin/cars/${carId}/blocks/${bid}`); toast.success("Block removed"); loadSchedule(); onMutate?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveRate = async () => {
    if (!Number(rate)) return toast.error("Enter a valid rate.");
    try { await api.put(`/admin/cars/${carId}/rate`, { weekly_rate: Number(rate) }); toast.success("Weekly rate updated"); onMutate?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-6 grid md:grid-cols-[280px_1fr] gap-5" data-testid="fleet-calendar">
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3">
            <Search size={15} className="text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="calendar-car-search" className="bg-transparent py-2.5 text-sm outline-none w-full" placeholder="Search vehicles…" />
          </div>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {filtered.map((c) => (
            <button key={c.id} onClick={() => { setCarId(c.id); setSelStart(null); setSelEnd(null); }} data-testid={`calendar-car-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${carId === c.id ? "bg-blue-50" : ""}`}>
              <p className="font-medium text-slate-900 text-sm truncate">{c.name}</p>
              <p className="text-slate-400 text-xs">${c.weekly_rate}/wk{c.review_status === "under_review" ? " · Under review" : ""}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-card p-6">
        {!car ? <p className="text-slate-400">Select a vehicle.</p> : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">{car.name}</h3>
                <p className="text-slate-400 text-sm flex items-center gap-1.5"><CalendarDays size={13} /> Click a start and end day to block a range</p>
              </div>
              <div className="flex items-end gap-2">
                <label className="block">
                  <span className="label-caps text-slate-400 block mb-1">Weekly rate ($)</span>
                  <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="calendar-rate-input" className="belo-input w-32 py-2" />
                </label>
                <button onClick={saveRate} data-testid="calendar-rate-save" className="bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-slate-800 transition-colors">Save</button>
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button onClick={() => setMonth(addMonths(month, -1))} data-testid="calendar-prev" className="grid place-items-center w-9 h-9 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronLeft size={16} /></button>
              <p className="font-heading font-semibold text-slate-900">{format(month, "MMMM yyyy")}</p>
              <button onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next" className="grid place-items-center w-9 h-9 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronRight size={16} /></button>
            </div>

            <div className="grid grid-cols-7 gap-1.5 mt-4">
              {WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-semibold text-slate-400 py-1">{w}</div>)}
              {Array.from({ length: leading }).map((_, i) => <div key={`b${i}`} />)}
              {days.map((d) => {
                const st = dayStatus(d);
                const sel = inSel(d);
                const clickable = st === "open";
                const base = "aspect-square rounded-lg text-sm flex items-center justify-center font-medium transition-colors relative";
                const color = st === "booked" ? "bg-rose-100 text-rose-700" : st === "blocked" ? "bg-amber-100 text-amber-700" : "bg-white text-slate-700 border border-slate-100 hover:bg-blue-50";
                return (
                  <button key={ymd(d)} data-testid={`calendar-day-${ymd(d)}`} disabled={!clickable} onClick={() => clickDay(d)}
                    className={`${base} ${color} ${sel ? "ring-2 ring-blue-600 z-10" : ""} ${isToday(d) ? "font-bold" : ""} ${clickable ? "cursor-pointer" : "cursor-default"}`}>
                    {format(d, "d")}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-200" /> Booked</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-200" /> Blocked</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border border-slate-200" /> Open</span>
            </div>

            {selStart && (
              <div className="mt-5 bg-slate-50 rounded-xl p-4" data-testid="calendar-selection">
                <p className="text-sm text-slate-700">Block <b>{selStart}</b>{selEnd && selEnd !== selStart ? <> → <b>{selEnd}</b></> : ""}</p>
                <div className="flex gap-2 mt-3">
                  <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="calendar-note" className="belo-input py-2" placeholder="Note (e.g. Maintenance, Turo booking)" />
                  <button onClick={blockDates} disabled={saving} data-testid="calendar-block-btn" className="shrink-0 bg-blue-600 text-white px-5 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">{saving ? "Blocking…" : "Block"}</button>
                </div>
              </div>
            )}

            <div className="mt-6">
              <p className="label-caps text-slate-400 mb-3">Blocked periods</p>
              {schedule.blocks.length === 0 ? <p className="text-slate-400 text-sm">No manual blocks.</p> : (
                <div className="space-y-2">
                  {schedule.blocks.map((b) => (
                    <div key={b.id} data-testid={`cal-block-${b.id}`} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-2.5">
                      <div><p className="text-slate-900 text-sm font-medium">{b.start} → {b.end}</p>{b.reason && <p className="text-slate-500 text-xs">{b.reason}</p>}</div>
                      <button onClick={() => removeBlock(b.id)} data-testid={`cal-block-remove-${b.id}`} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
